import { BASE_URL } from "../globals";

export const DEFAULT_EMBED_WIDTH = "100%";
export const DEFAULT_EMBED_HEIGHT = "352px";
export const EMBED_DIMENSION_DEFAULT = "100%";
export const EMBED_PATH_PREFIX = "/video/c/embed";
export const EMBED_TIME_QUERY_KEY = "t";
const DEFAULT_EMBED_IFRAME_TITLE = "Washington Post video";

export const EMBED_DIMENSION_PRESETS = [
  { id: "compact", label: "Compact", width: "100%", height: "152px" },
  { id: "normal", label: "Normal", width: "100%", height: "352px" },
  { id: "large", label: "Large", width: "100%", height: "552px" }
];

/**
 * Normalizes embed width/height to "<number>px" or "<number>%".
 * Bare numbers become px; invalid or negative values become "100%".
 */
export const normalizeEmbedDimension = value => {
  const trimmed = String(value ?? "").trim();

  if (!trimmed) {
    return EMBED_DIMENSION_DEFAULT;
  }

  const percentMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*%$/i);

  if (percentMatch) {
    const num = Number(percentMatch[1]);

    if (num < 0) {
      return EMBED_DIMENSION_DEFAULT;
    }

    return `${num}%`;
  }

  const pxMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*px$/i);

  if (pxMatch) {
    const num = Number(pxMatch[1]);

    if (num < 0) {
      return EMBED_DIMENSION_DEFAULT;
    }

    return `${num}px`;
  }

  const numberMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)$/);

  if (numberMatch) {
    const num = Number(numberMatch[1]);

    if (num < 0) {
      return EMBED_DIMENSION_DEFAULT;
    }

    return `${num}px`;
  }

  return EMBED_DIMENSION_DEFAULT;
};

export const matchesEmbedPreset = (width, height, preset) =>
  normalizeEmbedDimension(width) === preset.width &&
  normalizeEmbedDimension(height) === preset.height;

export const secondsToTimestamp = totalSeconds => {
  const s = Math.floor(Number(totalSeconds) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;

  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  }

  return `${m}:${String(sec).padStart(2, "0")}`;
};

export const timestampToSeconds = value => {
  const parts = String(value || "")
    .trim()
    .split(":")
    .map(Number);

  if (parts.some(n => Number.isNaN(n))) {
    return 0;
  }

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  return parts[0] || 0;
};

/**
 * Merges optional start offset into embed query params for the dialog.
 * When disabled or zero, removes {@link EMBED_TIME_QUERY_KEY} from a shallow copy.
 */
export const mergeEmbedTimeQueryParam = (
  queryParams,
  startAtEnabled,
  startSeconds
) => {
  const next = { ...queryParams };

  if (startAtEnabled && startSeconds > 0) {
    next[EMBED_TIME_QUERY_KEY] = String(Math.floor(startSeconds));
  } else {
    delete next[EMBED_TIME_QUERY_KEY];
  }

  return next;
};

/**
 * Resolves embed settings from Orca config.embed with defaults.
 */
export const resolveEmbedConfig = (embedConfig = {}) => ({
  domain: embedConfig.domain ?? BASE_URL,
  defaultWidth: normalizeEmbedDimension(
    embedConfig.defaultWidth ?? DEFAULT_EMBED_WIDTH
  ),
  defaultHeight: normalizeEmbedDimension(
    embedConfig.defaultHeight ?? DEFAULT_EMBED_HEIGHT
  ),
  queryParams: embedConfig.queryParams ?? {},
  getEmbedUrlOverride: embedConfig.getEmbedUrlOverride
});

const buildEmbedQueryString = queryParams => {
  const entries = Object.entries(queryParams || {}).filter(
    ([, value]) => value != null && value !== ""
  );

  if (!entries.length) {
    return "";
  }

  const params = new URLSearchParams();
  entries.forEach(([key, value]) => params.append(key, String(value)));

  return `?${params.toString()}`;
};

/**
 * @returns Embed player URL for iframe src.
 */
export const getEmbedUrl = ({ videoId, domain, queryParams }) => {
  if (!videoId) {
    return "";
  }

  const resolvedDomain = domain ?? BASE_URL;
  const queryString = buildEmbedQueryString(queryParams);

  return `${resolvedDomain}${EMBED_PATH_PREFIX}/${videoId}${queryString}`;
};

/**
 * @returns Full iframe HTML for clipboard / display.
 */
export const getEmbedIframeHtml = ({
  videoId,
  width,
  height,
  domain,
  queryParams,
  title,
  getEmbedUrlOverride
}) => {
  const embedUrlParams = { videoId, domain, queryParams };
  const src =
    typeof getEmbedUrlOverride === "function"
      ? getEmbedUrlOverride(embedUrlParams)
      : getEmbedUrl(embedUrlParams);

  if (!src) {
    return "";
  }

  const resolvedWidth = width ?? DEFAULT_EMBED_WIDTH;
  const resolvedHeight = height ?? DEFAULT_EMBED_HEIGHT;
  const normalizedTitle =
    String(title ?? "").trim() || DEFAULT_EMBED_IFRAME_TITLE;

  return `<iframe src="${src}" width="${resolvedWidth}" height="${resolvedHeight}" style="border: none;" allow="fullscreen; clipboard-write; clipboard-read" title="${normalizedTitle}"></iframe>`;
};
